import {
  BackButton,
  Button,
  ImageWithRetry,
  Loader,
} from "@src/components/atoms";
import { useDCContext } from "@src/context/dcContext";
import { useOperatorContext } from "@src/context/operatorContext";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import { ONBOARDING_COMMITMENT_OPTIONS } from "@src/constants/constants";
import { getSocialProofSentence } from "@src/components/utility/textFormatterHelpers";

export const OnboardingCommitmentConfirmation = ({
  onNext,
  onBack,
  step,
  previous,
  submitAction,
  commitment,
}) => {
  const { getDynamicReplacement } = useDCContext();
  const { profile, getFirstName, getLastName } = useOperatorContext();
  const { operatorMetadata } = profile;

  const commitmentKey =
    commitment ||
    operatorMetadata?.find((c) => c.key === "onboarding_commitment")?.value;

  const commitmentOptions = ONBOARDING_COMMITMENT_OPTIONS;

  const {
    data: { customerCommitments } = {},
    isPending: commitmentsPending,
    error: commitmentsError,
  } = OperatorHooks.useCustomerCommitments(profile?.operator?.id);

  const commitmentPercentage = getSocialProofSentence(
    null,
    commitmentKey,
    customerCommitments,
    profile?.customer,
  );

  const handleNext = () => {
    const request = {
      operatorCode: profile.operator.code,
      firstname: getFirstName(),
      lastname: getLastName(),
      metadata: [
        {
          key: "opt_in_accepted_level_1",
          value: true,
          type: "BOOLEAN",
        },
        {
          key: "opt_in_accepted_level_2",
          value:
            operatorMetadata?.find((c) => c.key === "opt_in_accepted_level_2")
              ?.value ?? true,
          type: "BOOLEAN",
        },
        {
          key: "opt_in_accepted_level_3",
          value:
            operatorMetadata?.find((c) => c.key === "opt_in_accepted_level_3")
              ?.value ?? true,
          type: "BOOLEAN",
        },
      ],
    };
    submitAction && submitAction(request);
    onNext();
  };

  return (
    <div
      className={`h-full flex flex-col sm:px-2 animate-animated animate-fast ${
        previous < step ? "animate-fadeInRight" : "animate-fadeInLeft"
      }`}
    >
      <div className="grow h-full text-center">
        {!!commitmentKey && (
          <div className="flex justify-center py-6 animate-animated animate-fadeInDown animate-fast">
            <div className="bg-primary-700 w-max-content rounded-lg shadow text-base border border-transparent px-6 py-2 font-bold text-white">
              {getDynamicReplacement(commitmentOptions[commitmentKey]?.label)}
            </div>
          </div>
        )}
        {commitmentsPending ? (
          <div className="min-h-20">
            <div className="w-full h-full flex items-center justify-center">
              <Loader size="xl" />
            </div>
          </div>
        ) : commitmentsError ? (
          <div>Error fetching commitments...</div>
        ) : (
          <div className="flex justify-evenly items-center px-1 py-6 min-h-24 text-center">
            {customerCommitments?.length > 0 && (
              <div className="animate-animated animate-fadeInRight animate-fast">
                <div className="text-5xl md:text-7xl font-bold">
                  {commitmentPercentage}
                </div>
                <p className="text-base font-bold px-1 sm:px-6 md:px-8">
                  {getDynamicReplacement("onboarding__step5__subcontent")}
                </p>
              </div>
            )}
            {!!commitmentKey && (
              <ImageWithRetry
                className="h-32 md:h-40 mx-auto animate-animated animate-fadeIn animate-fast"
                src={getDynamicReplacement(
                  commitmentOptions[commitmentKey]?.image,
                )}
                alt=""
              />
            )}
          </div>
        )}
        <div className="flex justify-center py-4">
          <p className="text-base font-medium">
            {getDynamicReplacement("onboarding__step5__maintain")}
          </p>
        </div>
      </div>
      <div className="flex justify-center items-center pt-6 md:pt-8 relative">
        <div className="absolute left-0 p-1">
          <BackButton onCustomBack={onBack} noContainer />
        </div>
        <div>
          <Button
            onClick={handleNext}
            className="px-4 py-2"
            children={getDynamicReplacement(
              "onboarding__texts__explore_my_account",
            )}
          />
        </div>
      </div>
    </div>
  );
};
